import { Fragment, useState } from 'react'
import {
  addGroupMember,
  canAddGroupMember,
  canInsert,
  insertIndex,
  insertWorkflow,
  MAX_GROUP_MEMBERS,
  MAX_PARTICIPANTS,
  personById,
  removeParticipant,
  removeStage,
  signingOrder,
  stageForSelection,
} from '../workflow/draft'
import {
  type InsertKind,
  type WorkflowDraft,
  type WorkflowParticipant,
  WORKFLOW_TONES,
} from '../workflow/types'

const ROLE_LABELS: Record<WorkflowParticipant['role'], string> = {
  signer: 'Signer',
  witness: 'Witness',
  cc: 'Receives a copy',
  needs_to_view: 'Needs to view',
}

type StageKind = 'order' | 'parallel' | 'group' | 'branch' | 'notify'

const STAGE_KIND: Record<StageKind, string> = {
  order: 'One at a time',
  parallel: 'In parallel',
  group: 'Signing group',
  branch: 'Conditional',
  notify: 'Notified when done',
}

type PersonStatus = 'done' | 'active' | 'waiting' | 'skipped'

function personStatus(draft: WorkflowDraft, id: string, order: string[]): PersonStatus {
  const active = order.indexOf(draft.activeId ?? '')
  const here = order.indexOf(id)
  if (here < 0) return 'waiting'
  if (draft.activeId === id) return 'active'
  if (active > here) return 'done'
  return 'waiting'
}

function roleBadge(role: WorkflowParticipant['role']) {
  if (role === 'signer') return null
  return <span className="wf-role-badge">{ROLE_LABELS[role]}</span>
}

function Connector({ dashed = false }: { dashed?: boolean }) {
  return <div className={`wf-connector${dashed ? ' wf-connector-dashed' : ''}`} aria-hidden />
}

function TerminalNode({ kind, children }: { kind: 'start' | 'end'; children: React.ReactNode }) {
  return <div className={`wf-node wf-node-${kind}`}>{children}</div>
}

function StageBlock({
  kind,
  title,
  detail,
  selected,
  onSelect,
  className,
  solo = false,
  children,
}: {
  kind: StageKind
  title: string
  detail?: string
  selected?: boolean
  onSelect?: () => void
  className?: string
  solo?: boolean
  children: React.ReactNode
}) {
  const Tag = onSelect ? 'button' : 'div'
  return (
    <div className={`wf-stage${className ? ` ${className}` : ''}`}>
      <Tag
        type={onSelect ? 'button' : undefined}
        className={`wf-stage-block wf-stage-${kind}${solo ? ' wf-stage-solo' : ''}${selected ? ' is-selected' : ''}`}
        onClick={onSelect}
      >
        {solo ? (
          <div className="wf-stage-top">
            <span className="wf-stage-kind">{STAGE_KIND[kind]}</span>
            <span className="wf-stage-title">{title}</span>
            {detail && <span className="wf-stage-detail">{detail}</span>}
          </div>
        ) : (
          <>
            <div className="wf-stage-head">
              <span className="wf-stage-kind">{STAGE_KIND[kind]}</span>
              {detail && <span className="wf-stage-detail">{detail}</span>}
            </div>
            <div className="wf-stage-title">{title}</div>
          </>
        )}
        <div className="wf-stage-body">{children}</div>
      </Tag>
    </div>
  )
}

function PersonNode({
  participant,
  selected,
  onSelect,
  onRemove,
  status,
  subtitle,
  removeLabel = 'Remove from workflow',
}: {
  participant: WorkflowParticipant
  selected?: boolean
  onSelect?: () => void
  onRemove?: () => void
  status?: PersonStatus
  subtitle?: string
  removeLabel?: string
}) {
  const Tag = onSelect ? 'button' : 'div'
  return (
    <Tag
      type={onSelect ? 'button' : undefined}
      className={`wf-node wf-node-person${selected ? ' is-selected' : ''}${status ? ` is-${status}` : ''}`}
      onClick={onSelect}
    >
      {onRemove && (
        <span
          role="button"
          tabIndex={0}
          className="wf-node-remove"
          aria-label={removeLabel}
          onClick={(e) => { e.stopPropagation(); onRemove() }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onRemove() }
          }}
        >×</span>
      )}
      <span className="wf-node-tone" style={{ background: WORKFLOW_TONES[participant.tone] }} aria-hidden />
      <span className="wf-node-label">{participant.name.trim() || ROLE_LABELS[participant.role]}</span>
      <span className="wf-node-sub">{subtitle ?? participant.email}</span>
      {!subtitle && roleBadge(participant.role)}
      {status === 'active' && <span className="wf-node-status">Signing now</span>}
      {status === 'done' && <span className="wf-node-status wf-node-status-done">Signed</span>}
      {status === 'skipped' && <span className="wf-node-status wf-node-status-skip">Skipped</span>}
    </Tag>
  )
}

/** Why an insert is off: the envelope has as many people as the server takes. */
const FULL = `An envelope has room for ${MAX_PARTICIPANTS} people, a signing group's members included`

/**
 * The pills between two steps of the canvas: what can be added there. Only
 * what still fits is offered, and nothing at all once nothing fits. They were
 * disabled instead, and a disabled pill looked like an enabled one; the
 * buttons above the canvas keep the sentence that says why (FULL).
 */
export function InsertSlot({
  onInsert,
  allowed,
}: {
  onInsert: (kind: InsertKind) => void
  allowed: (kind: InsertKind) => boolean
}) {
  const kinds = ([
    ['signer', '+ Signer', 'Add signer here'],
    ['branch', '+ Branch', 'Add branch here'],
    ['group', '+ Group', 'Add group here'],
    ['notify', '+ Notified', 'Add copy recipient here'],
  ] as const).filter(([kind]) => allowed(kind))
  if (kinds.length === 0) return null
  return (
    <div className="wf-insert">
      {kinds.map(([kind, label, title]) => (
        <button key={kind} type="button" className="wf-insert-btn" onClick={() => onInsert(kind)} title={title}>
          {label}
        </button>
      ))}
    </div>
  )
}

function SigningChain({
  draft,
  ids,
  order,
  selectedId,
  onSelect,
  onRemove,
}: {
  draft: WorkflowDraft
  ids: string[]
  order: string[]
  selectedId: string | null
  onSelect: (id: string) => void
  onRemove: (id: string) => void
}) {
  return (
    <div className="wf-chain">
      {ids.map((id, i) => {
        const p = personById(draft, id)
        if (!p) return null
        return (
          <div key={id} className="wf-chain-step">
            {i > 0 && <span className="wf-chain-arrow" aria-hidden>→</span>}
            <PersonNode
              participant={p}
              selected={selectedId === id}
              onSelect={() => onSelect(id)}
              onRemove={() => onRemove(id)}
              status={personStatus(draft, id, order)}
            />
          </div>
        )
      })}
    </div>
  )
}

function NotifyLane({
  draft,
  ids,
  selectedId,
  onSelect,
  onRemove,
}: {
  draft: WorkflowDraft
  ids: string[]
  selectedId: string | null
  onSelect: (id: string) => void
  onRemove: (id: string) => void
}) {
  return (
    <div className="wf-notify-lane">
      {ids.map((id) => {
        const p = personById(draft, id)
        if (!p) return null
        return (
          <PersonNode
            key={id}
            participant={p}
            selected={selectedId === id}
            onSelect={() => onSelect(id)}
            onRemove={() => onRemove(id)}
            subtitle={ROLE_LABELS[p.role]}
            removeLabel="Remove copy recipient"
          />
        )
      })}
    </div>
  )
}

function renderStage(
  draft: WorkflowDraft,
  stage: WorkflowDraft['stages'][number],
  order: string[],
  selectedId: string | null,
  onSelect: (id: string) => void,
  onRemoveParticipant: (id: string) => void,
) {
  switch (stage.kind) {
    case 'solo': {
      const p = personById(draft, stage.participantId)
      if (!p) return null
      return (
        <StageBlock kind="order" title={stage.title} solo>
          <PersonNode
            participant={p}
            selected={selectedId === p.id}
            onSelect={() => onSelect(p.id)}
            onRemove={() => onRemoveParticipant(p.id)}
            status={personStatus(draft, p.id, order)}
          />
        </StageBlock>
      )
    }
    case 'chain':
      return (
        <StageBlock kind="order" title={stage.title} detail={stage.detail}>
          <SigningChain
            draft={draft}
            ids={stage.participantIds}
            order={order}
            selectedId={selectedId}
            onSelect={onSelect}
            onRemove={onRemoveParticipant}
          />
        </StageBlock>
      )
    case 'group':
      return (
        <StageBlock
          kind="group"
          title={stage.title}
          detail={stage.detail}
          selected={selectedId === `group:${stage.id}`}
          onSelect={() => onSelect(`group:${stage.id}`)}
        >
          <div className="wf-group-lane">
            {stage.participantIds.map((id) => {
              const p = personById(draft, id)
              if (!p) return null
              return (
                <PersonNode
                  key={id}
                  participant={p}
                  selected={selectedId === id}
                  onSelect={() => onSelect(id)}
                  onRemove={() => onRemoveParticipant(id)}
                />
              )
            })}
          </div>
        </StageBlock>
      )
    case 'branch': {
      const after = personById(draft, stage.afterParticipantId)
      const target = personById(draft, stage.targetParticipantId)
      if (!target) return null
      return (
        <StageBlock
          kind="branch"
          title={stage.title}
          detail={stage.detail}
          selected={selectedId === stage.branchId}
          onSelect={() => onSelect(stage.branchId)}
        >
          <p className="wf-branch-lead small faint">
            Checked after {after?.name.trim() || 'prior signer'} signs
          </p>
          <div className="wf-branch-fork">
            <div className="wf-branch-arm">
              <span className="wf-branch-tag">Yes · over limit</span>
              <PersonNode
                participant={target}
                selected={selectedId === target.id}
                onSelect={() => onSelect(target.id)}
                onRemove={() => onRemoveParticipant(target.id)}
                status={personStatus(draft, target.id, order)}
                subtitle="Must sign"
              />
            </div>
            <div className="wf-branch-arm">
              <span className="wf-branch-tag wf-branch-tag-skip">No · under limit</span>
              <PersonNode participant={target} status="skipped" subtitle="Skipped · recorded in audit" />
            </div>
          </div>
          <div className="wf-branch-merge"><span>Paths join</span></div>
        </StageBlock>
      )
    }
    case 'notify':
      return (
        <StageBlock kind="notify" title={stage.title}>
          <NotifyLane
            draft={draft}
            ids={stage.participantIds}
            selectedId={selectedId}
            onSelect={onSelect}
            onRemove={onRemoveParticipant}
          />
        </StageBlock>
      )
    default:
      return null
  }
}

function Inspector({
  draft,
  selectedId,
  onDraftChange,
  onSelect,
}: {
  draft: WorkflowDraft
  selectedId: string | null
  onDraftChange: (next: WorkflowDraft) => void
  onSelect: (id: string | null) => void
}) {
  const stage = stageForSelection(draft, selectedId)
  const person = selectedId ? personById(draft, selectedId) : null
  const branchStage = stage?.kind === 'branch' ? stage : null
  const groupStage = stage?.kind === 'group' ? stage : selectedId?.startsWith('group:')
    ? draft.stages.find((s) => s.id === selectedId.slice(6) && s.kind === 'group')
    : null

  function patchParticipant(id: string, change: Partial<WorkflowParticipant>) {
    onDraftChange({
      ...draft,
      participants: draft.participants.map((p) => (p.id === id ? { ...p, ...change } : p)),
    })
  }

  if (branchStage) {
    return (
      <div className="card">
        <h3>Routing rule</h3>
        <p className="small muted">Evaluated after the trigger signer completes their fields.</p>
        <label htmlFor="wf-field">When field</label>
        <input id="wf-field" type="text" value={branchStage.field}
          onChange={(e) => onDraftChange({
            ...draft,
            stages: draft.stages.map((s) => (
              s.kind === 'branch' && s.branchId === branchStage.branchId
                ? { ...s, field: e.target.value, title: `If ${e.target.value} is over ${s.value}` }
                : s
            )),
          })} />
        <label htmlFor="wf-val" style={{ marginTop: '.75rem' }}>When over</label>
        <input id="wf-val" type="text" value={branchStage.value}
          onChange={(e) => onDraftChange({
            ...draft,
            stages: draft.stages.map((s) => (
              s.kind === 'branch' && s.branchId === branchStage.branchId
                ? { ...s, value: e.target.value, title: `If ${s.field} is over ${e.target.value}` }
                : s
            )),
          })} />
        <button type="button" className="btn btn-ghost btn-block small" style={{ marginTop: '.75rem' }}
          onClick={() => { onDraftChange(removeStage(draft, branchStage.id)); onSelect(null) }}>
          Remove branch
        </button>
      </div>
    )
  }

  if (groupStage && groupStage.kind === 'group') {
    return (
      <div className="card">
        <h3>Signing group</h3>
        <p className="small muted">{groupStage.detail}. Click × on a member to remove them.</p>
        <label htmlFor="wf-group-label">Group label</label>
        <input id="wf-group-label" type="text" value={groupStage.title}
          onChange={(e) => onDraftChange({
            ...draft,
            stages: draft.stages.map((s) => (
              s.id === groupStage.id && s.kind === 'group' ? { ...s, title: e.target.value, groupLabel: e.target.value } : s
            )),
          })} />
        <button type="button" className="btn btn-ghost btn-block small" style={{ marginTop: '.75rem' }}
          disabled={!canAddGroupMember(draft, groupStage.id)}
          title={groupStage.participantIds.length >= MAX_GROUP_MEMBERS
            ? `A signing group has at most ${MAX_GROUP_MEMBERS} members`
            : canAddGroupMember(draft, groupStage.id) ? undefined : FULL}
          onClick={() => onDraftChange(addGroupMember(draft, groupStage.id))}>
          + Add member
        </button>
        <button type="button" className="btn btn-ghost btn-block small" style={{ marginTop: '.35rem' }}
          onClick={() => { onDraftChange(removeStage(draft, groupStage.id)); onSelect(null) }}>
          Remove whole group
        </button>
      </div>
    )
  }

  if (person) {
    return (
      <div className="card">
        <h3>{person.name.trim() || ROLE_LABELS[person.role]}</h3>
        <label htmlFor="wf-name">Name</label>
        <input id="wf-name" type="text" value={person.name}
          onChange={(e) => patchParticipant(person.id, { name: e.target.value })} />
        <label htmlFor="wf-email">Email</label>
        <input id="wf-email" type="email" value={person.email}
          onChange={(e) => patchParticipant(person.id, { email: e.target.value })} />
        <label htmlFor="wf-role">Role</label>
        <select id="wf-role" value={person.role}
          onChange={(e) => patchParticipant(person.id, { role: e.target.value as WorkflowParticipant['role'] })}>
          <option value="signer">Signer</option>
          <option value="witness">Witness</option>
          <option value="cc">Receives a copy</option>
          <option value="needs_to_view">Needs to view</option>
        </select>
        <button type="button" className="btn btn-ghost btn-block small" style={{ marginTop: '.75rem' }}
          onClick={() => {
            onDraftChange(removeParticipant(draft, person.id))
            if (selectedId === person.id) onSelect(null)
          }}>
          Remove from workflow
        </button>
      </div>
    )
  }

  return <WorkflowHint full={!INSERT_KINDS.some((kind) => canInsert(draft, kind))} />
}

const INSERT_KINDS = ['signer', 'branch', 'group', 'notify'] as const

/**
 * What the side panel says while no step is selected. On a full canvas the
 * + slots are gone (InsertSlot offers nothing that does not fit), so it says
 * why instead of pointing at them.
 */
export function WorkflowHint({ full }: { full: boolean }) {
  return (
    <div className="card">
      <h3>Workflow</h3>
      <p className="small muted" style={{ marginBottom: 0 }}>
        {full
          ? `Select a step to edit it. ${FULL}.`
          : 'Select a step to edit it, or use the + slots between stages to insert one.'}
      </p>
    </div>
  )
}

export function WorkflowBuilder({
  draft,
  onDraftChange,
  activeStep = 'workflow',
  onContinue,
  onUseSimpleList,
  continueLabel = 'Continue to place fields',
}: {
  draft: WorkflowDraft
  onDraftChange: (next: WorkflowDraft) => void
  activeStep?: 'upload' | 'workflow' | 'place' | 'review'
  onContinue: () => void
  onUseSimpleList?: () => void
  continueLabel?: string
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const order = signingOrder(draft)
  const branchCount = draft.stages.filter((s) => s.kind === 'branch').length

  function insert(kind: InsertKind, at: number) {
    onDraftChange(insertWorkflow(draft, kind, at))
  }

  return (
    <div className="wf-send-builder">
      <div className="wf-builder-toolbar">
        <div className="wf-step-tabs" role="tablist" aria-label="Send flow steps">
          <span className={`wf-step-tab${activeStep === 'upload' ? ' is-done' : ''}`}>Upload</span>
          <span className={`wf-step-tab${activeStep === 'workflow' ? ' is-active' : activeStep !== 'upload' ? ' is-done' : ''}`}>Workflow</span>
          <span className={`wf-step-tab${activeStep === 'place' ? ' is-active' : ''}`}>Place fields</span>
          <span className={`wf-step-tab${activeStep === 'review' ? ' is-active' : ''}`}>Review</span>
        </div>
        <div className="row-tight">
          {([['signer', '+ Signer'], ['branch', '+ Branch'], ['group', '+ Group'], ['notify', '+ Notified']] as const)
            .map(([kind, label]) => (
              <button key={kind} type="button" className="btn btn-ghost small"
                disabled={!canInsert(draft, kind)} title={canInsert(draft, kind) ? undefined : FULL}
                onClick={() => insert(kind, insertIndex(draft, selectedId))}>{label}</button>
            ))}
        </div>
      </div>
      <div className="wf-send-body">
        <div className="wf-canvas-wrap">
          <div className="wf-canvas" aria-label="Signing workflow">
            <TerminalNode kind="start">
              <span className="wf-node-label">Send</span>
              <span className="wf-node-sub">{draft.document}</span>
            </TerminalNode>
            <Connector />
            <InsertSlot onInsert={(kind) => insert(kind, 0)} allowed={(kind) => canInsert(draft, kind)} />
            {draft.stages.map((stage, i) => (
              <Fragment key={stage.id}>
                {renderStage(draft, stage, order, selectedId, setSelectedId, (id) => {
                  onDraftChange(removeParticipant(draft, id))
                  if (selectedId === id) setSelectedId(null)
                })}
                <Connector dashed={stage.kind === 'notify' || draft.stages[i + 1]?.kind === 'notify'} />
                <InsertSlot onInsert={(kind) => insert(kind, i + 1)} allowed={(kind) => canInsert(draft, kind)} />
              </Fragment>
            ))}
            <Connector />
            <TerminalNode kind="end">
              <span className="wf-node-label">Complete</span>
              <span className="wf-node-sub">Certificate issued · PDF sealed</span>
            </TerminalNode>
          </div>
        </div>
        <aside className="wf-send-aside stack">
          <Inspector
            draft={draft}
            selectedId={selectedId}
            onDraftChange={onDraftChange}
            onSelect={setSelectedId}
          />
          <div className="card card-tight small muted">
            <div className="row-between">
              <span>Participants</span><span>{draft.participants.length} of {MAX_PARTICIPANTS}</span>
            </div>
            <div className="row-between"><span>Stages</span><span>{draft.stages.length}</span></div>
            <div className="row-between"><span>Branches</span><span>{branchCount}</span></div>
          </div>
          <button type="button" className="btn btn-block" onClick={onContinue}>{continueLabel}</button>
          {onUseSimpleList && (
            <button type="button" className="btn btn-ghost btn-block small" onClick={onUseSimpleList}>
              Use the simple list instead
            </button>
          )}
        </aside>
      </div>
    </div>
  )
}
